import capitalize from 'lodash/capitalize';

import { ApiValidationError, hasErrorDetails, isAxiosError } from './api-types';

type MappedFieldViolation = {
  [key: string]: string | MappedFieldViolation;
};

const convertSnakeCaseToCamelCase = (value: string): string => {
  return value.replace(/_([a-zA-Z0-9])/g, (_, character) =>
    character.toUpperCase()
  );
};

export const is403Error = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    return error?.response?.status === 403;
  }
  return false;
};

export const parseApiError = (
  error: unknown,
  fallbackMessage = 'Oops! Something went wrong! Please try again later.'
): string => {
  if (isAxiosError(error)) {
    const axiosError = error?.response?.data;

    if (axiosError && hasErrorDetails(axiosError)) {
      if (axiosError?.fieldViolations?.length) {
        // Always show only first error
        return capitalize(axiosError.fieldViolations[0].description);
      }

      return capitalize(axiosError.message) ?? fallbackMessage;
    }
  }

  return fallbackMessage;
};

export const getFlattenedFieldViolationErrors = (
  error: unknown,
  prefixToRemove = ''
): Record<string, string> | null => {
  if (!isAxiosError(error)) {
    return null;
  }

  const axiosError = error?.response?.data;

  if (!hasErrorDetails(axiosError)) {
    return null;
  }

  const { fieldViolations, message } = (axiosError as ApiValidationError) ?? {};

  if (!fieldViolations?.length) {
    if (message) {
      return { [convertSnakeCaseToCamelCase('')]: capitalize(message) };
    }
    return null;
  }

  return (
    fieldViolations.reduce((acc, { field, description }) => {
      const fieldName = field.startsWith(prefixToRemove)
        ? field.replace(prefixToRemove, '')
        : field;
      acc[convertSnakeCaseToCamelCase(fieldName)] = capitalize(description);

      return acc;
    }, {} as Record<string, string>) ?? null
  );
};

export const getNestedFieldViolationErrors = (
  error: unknown
): MappedFieldViolation | null => {
  if (!isAxiosError(error)) {
    return null;
  }

  const axiosError = error?.response?.data as ApiValidationError;

  if (!hasErrorDetails(axiosError)) {
    return null;
  }

  const { fieldViolations } = axiosError ?? {};
  const mappedFieldViolation: MappedFieldViolation = {};

  if (!fieldViolations?.length) {
    return null;
  }

  for (const { field, description } of fieldViolations) {
    const keys = field.split('.');

    let _mappedFieldViolation = mappedFieldViolation;

    keys.forEach((key, index) => {
      const _key = convertSnakeCaseToCamelCase(key);

      if (index === keys.length - 1) {
        _mappedFieldViolation[_key] = capitalize(description);
      } else {
        if (
          typeof _mappedFieldViolation[_key] !== 'object' ||
          _mappedFieldViolation[_key] === null ||
          Array.isArray(_mappedFieldViolation[_key])
        ) {
          _mappedFieldViolation[_key] = {};
        }

        _mappedFieldViolation = _mappedFieldViolation[_key];
      }
    });
  }

  return mappedFieldViolation;
};
